<template>
  <div
    :class="{
      'calendar-sidebar': true,
      'has-visible-notes': notesVisible
    }">
    <d-button-group size="small" class="calendar-sidebar-ordering-toggle-ui">
      <d-button
        :outline="aircraftsOrdering !== 'live'"
        @click.prevent="setAircraftsOrdering('live')">
        Live
      </d-button>
      <d-button
        :outline="aircraftsOrdering !== 'alphabetical'"
        @click.prevent="setAircraftsOrdering('alphabetical')">
        A-Z
      </d-button>
      <d-button
        :outline="aircraftsOrdering !== 'custom'"
        @click.prevent="setAircraftsOrdering('custom')">
        Custom
      </d-button>
    </d-button-group>

    <d-button
      theme="danger"
      @click="toggleWarnings"
      class="calendar-sidebar-warning-toggle-ui">
      <i class="material-icons">warning</i>&nbsp;
      <template v-if="warningsVisible">Hide warnings ({{ warningsCount }})</template>
      <template v-else>Show warnings ({{ warningsCount }})</template>
    </d-button>

    <ul
      v-if="warningsVisible && warnings['HIGH'] && warnings['HIGH'].length"
      :class="{ 'calendar-sidebar-warnings-list': true, 'is-loading': !warningsLoaded }">
      <li
        v-for="(warning, index) of warnings['HIGH']"
        :key="'calendar-sidebar-warning-item-high-' + index"
        class="calendar-sidebar-warning-item-high">
          <strong class="calendar-sidebar-warning-item-high text-danger">
            <i class="material-icons">warning</i> HIGH
          </strong>
      </li>
    </ul>

    <ul
      v-if="warningsVisible && warnings['MEDIUM'] && warnings['MEDIUM'].length"
      :class="{ 'calendar-sidebar-warnings-list': true, 'is-loading': !warningsLoaded }">
      <li
        v-for="(warning, index) of warnings['MEDIUM']"
        :key="'calendar-sidebar-warning-item-medium-' + index"
        class="calendar-sidebar-warning-item-medium">
          <strong class="calendar-sidebar-warning-item-medium text-warning">
            <i class="material-icons">warning</i> MEDIUM
          </strong>
      </li>
    </ul>

    <template v-if="aircraftsOrdering === 'custom'">
      <draggable
        tag="ul"
        v-model="aircraftsClone"
        :class="{
          'calendar-sidebar-aircrafts-list': true,
          'is-loading': !aircraftsLoaded
        }">
        <li
          v-for="(aircraft, index) of aircraftsClone"
          :key="'calendar-sidebar-aircraft-item-' + index"
          :class="{
            'calendar-sidebar-aircraft-item': true,
            'is-selected': selectedAircraft === aircraft.id
          }"
          @click="toggleSelectedAircraft(aircraft.id)">
          <div class="calendar-sidebar-aircraft-item-wrapper">
            <d-button
              size="sm"
              pill
              outline
              :class="{
                'calendar-sidebar-aircraft-item-add-note': true,
                'has-notes': !!aircraft.calendar_note
              }"
              @click.prevent.stop="addAircraftNote(aircraft.id, aircraft.calendar_note)">
              <i class="material-icons">text_snippet</i>

              <div
                v-if="aircraft.calendar_note"
                class="calendar-sidebar-aircraft-item-add-note-tooltip"
                v-pure-html="nl2br(aircraft.calendar_note)">
              </div>
            </d-button>

            <d-button
              size="sm"
              pill
              outline
              class="calendar-sidebar-aircraft-item-add-trip"
              @click.prevent.stop="addTrip(aircraft.id)">
              <i class="material-icons">add</i>
            </d-button>

            <strong class="calendar-sidebar-aircraft-item-name">
              {{ aircraft.regin_mark }} <small>{{ aircraft.icao_type }}</small>
            </strong>

            <aircraft-additional-info :aircraft="aircraft" />
          </div>
        </li>
      </draggable>
    </template>
    <template v-else>
      <ul :class="{ 'calendar-sidebar-aircrafts-list': true, 'is-loading': !aircraftsLoaded }">
        <li
          v-for="(aircraft, index) of aircrafts"
          :key="'calendar-sidebar-aircraft-item-' + index"
          :class="{ 'calendar-sidebar-aircraft-item': true, 'is-selected': selectedAircraft === aircraft.id }"
          @click="toggleSelectedAircraft(aircraft.id)">
          <div class="calendar-sidebar-aircraft-item-wrapper">
            <d-button
              size="sm"
              pill
              outline
              :class="{
                'calendar-sidebar-aircraft-item-add-note': true,
                'has-notes': !!aircraft.calendar_note
              }"
              @click.prevent.stop="addAircraftNote(aircraft.id, aircraft.calendar_note)">
              <i class="material-icons">text_snippet</i>

              <div
                v-if="aircraft.calendar_note"
                class="calendar-sidebar-aircraft-item-add-note-tooltip"
                v-pure-html="nl2br(aircraft.calendar_note)">
              </div>
            </d-button>

            <d-button
              size="sm"
              pill
              outline
              class="calendar-sidebar-aircraft-item-add-trip"
              @click.prevent.stop="addTrip(aircraft.id)">
              <i class="material-icons">add</i>
            </d-button>

            <strong class="calendar-sidebar-aircraft-item-name">
              {{ aircraft.regin_mark }} <small>{{ aircraft.icao_type }}</small>
            </strong>

            <aircraft-additional-info :aircraft="aircraft" />
          </div>
        </li>
      </ul>
    </template>
  </div>
</template>

<script>
import Vue from 'vue';
import AircraftAdditionalInfo from './../../components/common/AircraftAdditionalInfo';
import vueDraggable from 'vuedraggable';

export default {
  name: 'calendar-sidebar',
  props: [
    'aircrafts',
    'aircraftsLoaded',
    'aircraftsOrdering',
    'flightsLoaded',
    'selectedAircraft',
    'notes',
    'notesLoaded',
    'notesVisible',
    'warnings',
    'warningsLoaded',
    'warningsVisible'
  ],
  components: {
    'aircraft-additional-info': AircraftAdditionalInfo,
    'draggable': vueDraggable
  },
  computed: {
    warningsCount () {
      if (!this.warnings) {
        return 0;
      }

      let sum = 0;

      if (this.warnings['HIGH']) {
        sum += this.warnings['HIGH'].slice().reduce((prev, next) => prev + next.length, 0);
      }

      if (this.warnings['MEDIUM']) {
        sum += this.warnings['MEDIUM'].slice().reduce((prev, next) => prev + next.length, 0);
      }

      return sum;
    }
  },
  data () {
    return {
      aircraftsClone: []
    };
  },
  watch: {
    aircrafts (newValue, oldValue) {
      if (newValue.map(item => item.id).join(',') === oldValue.map(item => item.id).join(',')) {
        return;
      }

      this.aircraftsClone = newValue.slice();
    }
  },
  mounted () {
    this.aircraftsClone = this.aircrafts.slice();
    this.$bus.$on('calendar-sidebar-update-aircrafts-clone', this.updateAircraftsClone);

    setTimeout(() => {
      this.$watch('aircraftsClone', (newValue, oldValue) => {
        if (this.aircraftsOrdering === 'custom') {
          if (newValue.map(item => item.id).join(',') === oldValue.map(item => item.id).join(',')) {
            return;
          }

          this.$bus.$emit('calendar-change-aircrafts-custom-ordering', this.aircraftsClone);
        }
      });
    }, 1000);
  },
  methods: {
    setAircraftsOrdering (ordering) {
      this.$bus.$emit('calendar-change-aircrafts-ordering', ordering);
    },
    toggleWarnings () {
      window.scrollTo(0, 0);
      this.$bus.$emit('calendar-toggle-warnings-visibility', !this.warningsVisible);
    },
    toggleSelectedAircraft (aircraftID) {
      this.$bus.$emit('calendar-toggle-selected-aircraft', aircraftID);
    },
    addTrip (aircraftID) {
      this.$router.push('/dashboard/trips/new/' + parseInt(aircraftID, 10));
    },
    addAircraftNote (aircraftID, aircraftNote) {
      if (!aircraftNote) {
        this.$bus.$emit('calendar-aircraft-note-popup-show', {
          id: aircraftID
        });
      } else {
        this.$bus.$emit('calendar-aircraft-note-popup-show', {
          id: aircraftID,
          note: aircraftNote
        });
      }
    },
    nl2br (textInput) {
      return textInput.replace(/\n/gmi, '<br>');
    },
    updateAircraftsClone () {
      Vue.nextTick(() => {
        this.aircraftsClone = this.aircrafts.slice();
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('calendar-sidebar-update-aircrafts-clone', this.updateAircraftsClone);
  }
};
</script>

<style lang="scss" scoped>
.calendar-sidebar {
  background: #f5f5f5;
  border-right: 1px solid #ddd;
  min-height: calc(100vh - 48px);
  padding: 90px 0 40px 0;
  position: relative;
  width: 200px;
  z-index: 10;

  &:before {
    background: #f5f5f5;
    border-bottom: 1px solid #ddd;
    content: "";
    height: 89px;
    left: 0;
    position: fixed;
    top: 49px;
    width: 200px;
    z-index: 1;
  }

  &.has-visible-notes{
    padding-top: 133px;

    &:before {
      height: 132px;
    }
  }

  &-warning-toggle-ui,
  &-ordering-toggle-ui {
    left: 100px;
    position: fixed;
    top: 55px;
    transform: translateX(-50%);
    z-index: 2;
  }

  &-warning-toggle-ui {
    top: 95px;
  }

  &-aircrafts-list,
  &-warnings-list {
    list-style-type: none;
    padding: 0;
    margin: 0;
    transition: opacity .25s ease-out;

    &.is-loading {
      opacity: .25;
    }
  }

  &-warning-item-high,
  &-warning-item-medium,
  &-warning-item-low {
    align-items: center;
    border-bottom: 1px solid #ddd;
    display: flex;
    height: 52px;
    justify-content: center;

    i {
      left: -5px;
      position: relative;
      top: 0;
    }
  }

  &-aircraft-item {
    align-items: center;
    border-top: 1px solid #ddd;
    cursor: pointer;
    display: flex;
    flex-wrap: wrap;
    height: 100px;
    padding: 0 10px;
    position: relative;

    &.is-selected {
      background: rgba(255, 252, 187, .33);
    }

    &:first-child {
      border-top: none;
    }

    &:last-child {
      border-bottom: 1px solid #ddd;
    }

    &-wrapper {
      display: block;
      width: 100%;
    }

    &-name {
      display: block;
      font-size: 16px;
      line-height: 1.2;
      margin-top: 6px;
      text-align: left;
      width: 100%;

      small {
        display: block;
        line-height: 1.2;
        margin-top: 3px;
        width: 100%;
      }
    }

    &-additional-info {
      display: block;
      font-size: 12px;
      width: 100%;
    }

    &-add-note {
      border: none;
      height: 20px;
      padding: 0;
      position: absolute;
      right: 40px;
      top: 5px;
      width: 20px;

      &:not(.has-notes) {
        color: #555;
        opacity: .25;
      }

      .calendar-sidebar-aircraft-item-add-note-tooltip {
        background: #fff;
        box-shadow: 0 5px 10px rgba(0, 0, 0, .25);
        color: #222;
        display: none;
        font-size: 13px;
        left: -10px;
        padding: 10px;
        position: absolute;
        text-align: left;
        top: 20px;
        width: 240px;
        z-index: 100;
      }

      &:active,
      &:focus,
      &:hover {
        background: transparent!important;
        box-shadow: none;

        i {
          color: #229dff;
        }

        .calendar-sidebar-aircraft-item-add-note-tooltip {
          display: block;
        }
      }

      &:not(.has-notes):hover {
        .calendar-sidebar-aircraft-item-add-note-tooltip {
          display: none;
        }
      }

      & > i {
        font-size: 20px;
        position: relative;
        top: 0;
      }
    }

    &-add-trip {
      height: 20px;
      padding: 0;
      position: absolute;
      right: 16px;
      top: 5px;
      width: 20px;

      & > i {
        font-size: 12px;
      }
    }
  }
}
</style>
